<template>
	<div id="entryForm">
		<!-- 顶部标题 -->
		<div class="entry-header">
			<h2>{{ meetingTitle || '会议入场验证' }}</h2>
			<p class="subtitle">请验证身份信息后进入会议室</p>
		</div>

		<!-- 步骤一：手机号验证 -->
		<div class="form-card" v-if="step === 'phone'">
			<div class="card-title">
				<i class="el-icon-mobile-phone"></i> 手机号验证
			</div>
			<el-form :model="phoneForm" label-position="top">
				<el-form-item label="手机号">
					<el-input v-model="phoneForm.phone" placeholder="请输入手机号"
						maxlength="11" clearable size="large">
						<i slot="prefix" class="el-icon-phone"></i>
					</el-input>
				</el-form-item>
				<el-button type="primary" :loading="verifying" @click="handleVerify"
					style="width:100%; margin-top: 10px;" round size="large">
					<i class="el-icon-check" v-if="!verifying"></i>
					{{ verifying ? '验证中...' : '验证并进入' }}
				</el-button>
			</el-form>
		</div>

		<!-- 步骤二：填写表单 -->
		<div class="form-card" v-if="step === 'form'">
			<div class="card-title">
				<i class="el-icon-edit-outline"></i> 完善个人信息
			</div>
			<p class="card-hint" v-if="hasExistingData">
				<i class="el-icon-info"></i> 已自动回填您的历史信息，请确认后提交
			</p>

			<el-form :model="entryFormData" label-position="top" ref="entryFormRef">
				<el-form-item v-for="field in formFields" :key="field.fieldKey"
					:label="field.fieldLabel" :prop="field.fieldKey"
					:rules="field.required ? [{ required: true, message: `请填写${field.fieldLabel}`, trigger: 'blur' }] : []">

					<!-- 文本输入 -->
					<el-input v-if="field.fieldType === 'TEXT' || field.fieldType === 'PHONE' || field.fieldType === 'ID_CARD'"
						v-model="entryFormData[field.fieldKey]"
						:placeholder="field.placeholder || `请输入${field.fieldLabel}`"
						clearable></el-input>

					<!-- 下拉选择 -->
					<el-select v-else-if="field.fieldType === 'SELECT'"
						v-model="entryFormData[field.fieldKey]"
						:placeholder="field.placeholder || `请选择${field.fieldLabel}`"
						style="width:100%">
						<el-option v-for="opt in parseOptions(field.options)" :key="opt"
							:label="opt" :value="opt"></el-option>
					</el-select>

					<!-- 多行文本 -->
					<el-input v-else-if="field.fieldType === 'TEXTAREA'"
						type="textarea" :rows="3"
						v-model="entryFormData[field.fieldKey]"
						:placeholder="field.placeholder || `请输入${field.fieldLabel}`"></el-input>

					<!-- 默认文本 -->
					<el-input v-else v-model="entryFormData[field.fieldKey]"
						:placeholder="field.placeholder || `请输入${field.fieldLabel}`"
						clearable></el-input>
				</el-form-item>

				<el-button type="primary" :loading="submitting" @click="handleSubmitForm"
					style="width:100%; margin-top: 10px;" round size="large">
					<i class="el-icon-right" v-if="!submitting"></i>
					{{ submitting ? '提交中...' : '确认并进入会议室' }}
				</el-button>
			</el-form>
		</div>

		<!-- 白名单用户 / 免验证跳转提示 -->
		<div class="form-card success-card" v-if="step === 'success'">
			<i class="el-icon-circle-check success-icon"></i>
			<p class="success-text">验证通过</p>
			<p class="success-hint">正在跳转到会议室...</p>
		</div>
	</div>
</template>

<script>
import { verifyMeetingEntry, submitEntryForm, getFormFields } from '@/request/api.js';

export default {
	name: 'EntryForm',
	data() {
		return {
			meetingId: null,
			meetingTitle: '',
			step: 'phone',       // phone → form → success
			verifying: false,
			submitting: false,
			hasExistingData: false,

			// 手机号表单
			phoneForm: {
				phone: ''
			},

			// 自定义表单字段定义（来自后端）
			formFields: [],

			// 用户填写的表单数据
			entryFormData: {},

			// 是否开启表单收集
			formCollectEnabled: true,
		}
	},
	created() {
		this.meetingId = this.$route.query.meetingId || this.$route.params.id;
		this.meetingTitle = this.$route.query.title || '会议入场验证';

		// 尝试从 localStorage 获取手机号自动填充
		const token = JSON.parse(localStorage.getItem('token') || '{}');
		if (token.phone) {
			this.phoneForm.phone = token.phone;
		}
	},
	methods: {
		/** 验证手机号 */
		async handleVerify() {
			if (!this.phoneForm.phone || this.phoneForm.phone.length !== 11) {
				this.$message.warning('请输入正确的11位手机号');
				return;
			}

			this.verifying = true;
			try {
				const res = await verifyMeetingEntry(this.meetingId, this.phoneForm.phone);
				if (res && res.code === 200) {
					const data = res.data;

					// 白名单用户 → 直接进入
					if (data.isWhitelisted) {
						this.step = 'success';
						setTimeout(() => this.goToMeetingRoom(), 1000);
						return;
					}

					// 不需要表单 → 直接进入
					if (!data.needForm) {
						this.step = 'success';
						setTimeout(() => this.goToMeetingRoom(), 1000);
						return;
					}

					// 需要填写表单 → 加载表单字段
					await this.loadFormFields();

					// 回填历史数据
					if (data.existingData && Object.keys(data.existingData).length > 0) {
						this.hasExistingData = true;
						this.entryFormData = { ...this.entryFormData, ...data.existingData };
					}

					this.step = 'form';
				} else {
					this.$message.error(res?.msg || '验证失败，请重试');
				}
			} catch (err) {
				console.error('入场验证失败:', err);
				this.$message.error('网络异常，请重试');
			} finally {
				this.verifying = false;
			}
		},

		/** 加载自定义表单字段 */
		async loadFormFields() {
			try {
				const res = await getFormFields(this.meetingId);
				if (res && res.code === 200) {
					this.formFields = res.data || [];
					// 初始化表单数据
					this.formFields.forEach(f => {
						if (!this.entryFormData[f.fieldKey]) {
							this.$set(this.entryFormData, f.fieldKey, '');
						}
					});
				}
			} catch (err) {
				console.error('加载表单字段失败:', err);
			}
		},

		/** 提交入场表单 */
		async handleSubmitForm() {
			// 表单校验
			if (this.$refs.entryFormRef) {
				try {
					await this.$refs.entryFormRef.validate();
				} catch (e) {
					this.$message.warning('请填写完整必填信息');
					return;
				}
			}

			this.submitting = true;
			try {
				const res = await submitEntryForm(this.meetingId, {
					phone: this.phoneForm.phone,
					formValues: this.entryFormData
				});
				if (res && res.code === 200) {
					this.step = 'success';
					this.$message.success('信息提交成功');
					setTimeout(() => this.goToMeetingRoom(), 1000);
				} else {
					this.$message.error(res?.msg || '提交失败');
				}
			} catch (err) {
				console.error('提交表单失败:', err);
				this.$message.error('提交失败，请重试');
			} finally {
				this.submitting = false;
			}
		},

		/** 跳转到会议室 */
		goToMeetingRoom() {
			this.$router.replace({
				name: 'meetingRoom',
				query: {
					meetingId: this.meetingId,
					title: this.meetingTitle,
					phone: this.phoneForm.phone
				}
			});
		},

		/** 解析 JSON 选项 */
		parseOptions(optionsStr) {
			try {
				return JSON.parse(optionsStr || '[]');
			} catch (e) {
				return [];
			}
		}
	}
}
</script>

<style lang="scss" scoped>
#entryForm {
	min-height: 100vh;
	background: linear-gradient(135deg, #f0f2f8 0%, #e8ecf4 100%);
	padding: 40px 20px;
	display: flex;
	flex-direction: column;
	align-items: center;

	.entry-header {
		text-align: center;
		margin-bottom: 32px;

		h2 {
			font-size: 22px;
			font-weight: 700;
			color: #1a1a2e;
			margin: 0 0 8px;
		}

		.subtitle {
			font-size: 14px;
			color: #8f9bb3;
			margin: 0;
		}
	}

	.form-card {
		width: 100%;
		max-width: 420px;
		background: #fff;
		border-radius: 16px;
		padding: 28px 24px;
		box-shadow: 0 4px 20px rgba(26, 26, 46, 0.06);

		.card-title {
			font-size: 16px;
			font-weight: 600;
			color: #1a1a2e;
			margin-bottom: 20px;
			display: flex;
			align-items: center;
			gap: 8px;

			i {
				font-size: 20px;
				color: #667eea;
			}
		}

		.card-hint {
			font-size: 13px;
			color: #67c23a;
			background: #f0f9eb;
			padding: 8px 12px;
			border-radius: 8px;
			margin-bottom: 16px;
			display: flex;
			align-items: center;
			gap: 6px;
		}
	}

	.success-card {
		text-align: center;
		padding: 60px 24px;

		.success-icon {
			font-size: 64px;
			color: #67c23a;
			margin-bottom: 16px;
		}

		.success-text {
			font-size: 20px;
			font-weight: 600;
			color: #1a1a2e;
			margin: 0 0 8px;
		}

		.success-hint {
			font-size: 14px;
			color: #8f9bb3;
			margin: 0;
		}
	}
}
</style>
